import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.asliapp.com';

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Asli Web AI. I can help you create a professional website in 2 minutes. What's your business called?"
    }
  ]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Persist conversation in sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem('asli_chat');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setMessages(parsed.messages);
        setConversationHistory(parsed.history);
      } catch (e) { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    if (messages.length > 1) {
      sessionStorage.setItem('asli_chat', JSON.stringify({
        messages,
        history: conversationHistory
      }));
    }
  }, [messages, conversationHistory]);

  const sendMessage = async (e) => {
    e.preventDefault();

    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);

    // Update display messages
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);

    // Build API conversation history
    const apiHistory = [...conversationHistory, { role: 'user', content: userMessage }];

    try {
      const response = await axios.post(`${API_BASE}/api/chat/onboarding`, {
        conversationHistory: apiHistory
      });

      const data = response.data;

      if (data.action === 'create_website') {
        // Claude wants to create the website - redirect to checkout
        const textContent = data.response.content?.find(c => c.type === 'text');
        const assistantText = textContent?.text || 'Great! Let me set up your website.';

        setMessages([...newMessages, { role: 'assistant', content: assistantText }]);

        setTimeout(() => {
          window.location.href = `/checkout?data=${encodeURIComponent(JSON.stringify(data.websiteData))}`;
        }, 1500);
        return;
      }

      // Extract assistant text from response
      let assistantText = '';
      const responseContent = data.response?.content;
      if (Array.isArray(responseContent)) {
        const textBlock = responseContent.find(c => c.type === 'text');
        assistantText = textBlock?.text || '';
      } else if (typeof responseContent === 'string') {
        assistantText = responseContent;
      } else if (data.response?.content?.[0]?.text) {
        assistantText = data.response.content[0].text;
      }

      // Update conversation history with what the server returned
      const updatedHistory = data.updatedHistory || [
        ...apiHistory,
        { role: 'assistant', content: data.response.content }
      ];

      setConversationHistory(updatedHistory);
      setMessages([...newMessages, { role: 'assistant', content: assistantText }]);

    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = error.response?.data?.error || 'Sorry, something went wrong. Please try again.';
      setMessages([...newMessages, {
        role: 'assistant',
        content: errorMsg
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    widget: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 1000
    },
    toggle: {
      background: '#2563eb',
      color: 'white',
      border: 'none',
      padding: '15px 25px',
      borderRadius: '50px',
      fontSize: '1rem',
      cursor: 'pointer',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.4)'
    },
    window: {
      width: '380px',
      height: '500px',
      background: 'white',
      borderRadius: '15px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      background: '#2563eb',
      color: 'white',
      padding: '15px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      color: 'white',
      fontSize: '1.2rem',
      cursor: 'pointer'
    },
    messagesArea: {
      flex: 1,
      overflowY: 'auto',
      padding: '15px'
    },
    message: (role) => ({
      display: 'flex',
      justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
      marginBottom: '10px'
    }),
    bubble: (role) => ({
      maxWidth: '80%',
      padding: '10px 15px',
      borderRadius: '15px',
      background: role === 'user' ? '#2563eb' : '#f1f3f5',
      color: role === 'user' ? 'white' : '#1a1a2e',
      fontSize: '0.9rem',
      lineHeight: '1.4',
      whiteSpace: 'pre-wrap'
    }),
    inputForm: {
      display: 'flex',
      padding: '10px',
      borderTop: '1px solid #e9ecef'
    },
    input: {
      flex: 1,
      padding: '10px',
      border: '1px solid #e9ecef',
      borderRadius: '20px',
      outline: 'none',
      fontSize: '0.9rem'
    },
    sendBtn: {
      background: '#2563eb',
      color: 'white',
      border: 'none',
      padding: '10px 15px',
      borderRadius: '20px',
      marginLeft: '8px',
      cursor: 'pointer',
      fontSize: '0.9rem'
    }
  };

  return (
    <div style={styles.widget}>
      {!isOpen && (
        <button
          style={styles.toggle}
          onClick={() => setIsOpen(true)}
        >
          Start Building
        </button>
      )}

      {isOpen && (
        <div style={styles.window}>
          <div style={styles.header}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>Asli Web AI Assistant</h3>
            <button style={styles.closeBtn} onClick={() => setIsOpen(false)}>X</button>
          </div>

          <div style={styles.messagesArea}>
            {messages.map((msg, index) => (
              <div key={index} style={styles.message(msg.role)}>
                <div style={styles.bubble(msg.role)}>
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && (
              <div style={styles.message('assistant')}>
                <div style={styles.bubble('assistant')}>
                  Typing...
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <form style={styles.inputForm} onSubmit={sendMessage}>
            <input
              style={styles.input}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
            />
            <button
              style={{
                ...styles.sendBtn,
                opacity: isLoading || !input.trim() ? 0.5 : 1
              }}
              type="submit"
              disabled={isLoading || !input.trim()}
            >
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default ChatWidget;
